<template>
  <transition name="mn-fade">
    <div
      v-if="shouldShow"
      class="maintenance-notifier"
      id="maintenance-notifier"
      role="status"
      aria-live="polite"
    >
      <span class="mn-message" v-html="message"></span>
      <button
        id="close-maintenance"
        class="close-maintenance"
        type="button"
        aria-label="Close maintenance notice"
        @click="dismiss"
      >
        ×
      </button>
    </div>
  </transition>
</template>

<script>
/**
 * MaintenanceNotifier.vue (Vue 2)
 *
 * Mirrors the Blade conditions:
 * - enabled
 * - domain (maintenanceNotifierDomain)
 * - now between start and end (exclusive)
 * - close (remove) behavior
 *
 * Extras:
 * - optional localStorage persistence so it stays closed until end time
 * - accepts ISO strings, timestamps, or Date objects for start/end
 */
export default {
  name: 'MaintenanceNotifier',
  props: {
    enabled: { type: Boolean, default: false },            // maintenanceNotifierEnabled
    domain: { type: Boolean, default: true },               // maintenanceNotifierDomain
    start: { type: [String, Number, Date], required: true },// maintenanceNotifierStartDate
    end: { type: [String, Number, Date], required: true },  // maintenanceNotifierEndDate
    message: { type: String, default: '' },                 // maintenanceNotifierMessage (HTML allowed)
    /** If true, store dismissal in localStorage until "end" time */
    rememberClose: { type: Boolean, default: true },
    /** Storage key used when rememberClose=true */
    storageKey: { type: String, default: 'maintenance-notifier-dismissed-until' },
    /** Optional: current time override for testing */
    now: { type: [String, Number, Date], default: null }
  },
  data () {
    return {
      dismissed: false,
      tick: Date.now() // simple reactive "now"; updates if we enable auto-ticking later
    }
  },
  computed: {
    startMs () { return this.toMs(this.start) },
    endMs () { return this.toMs(this.end) },
    nowMs () { return this.now ? this.toMs(this.now) : this.tick },

    inWindow () { return this.nowMs > this.startMs && this.nowMs < this.endMs },

    shouldShow () {
      if (!this.enabled) return false
      if (!this.domain) return false
      if (!this.inWindow) return false
      if (this.dismissed) return false
      return true
    }
  },
  created () {
    // restore dismissal persistence
    if (this.rememberClose) {
      try {
        const raw = localStorage.getItem(this.storageKey)
        const until = raw ? Number(raw) : 0
        if (until && this.nowMs < until) {
          this.dismissed = true
        }
      } catch (e) { /* ignore */ }
    }
    // Optional: if you want to keep "now" fresh in long sessions, uncomment:
    // this._timer = setInterval(() => { this.tick = Date.now() }, 30_000)
  },
  beforeDestroy () {
    if (this._timer) clearInterval(this._timer)
  },
  methods: {
    toMs (v) {
      if (v instanceof Date) return v.getTime()
      if (typeof v === 'number') return v
      // Treat string as ISO or parseable date
      const t = Date.parse(v)
      return isNaN(t) ? Date.now() : t
    },
    dismiss () {
      this.dismissed = true
      this.$emit('closed')
      if (this.rememberClose) {
        try {
          // remember until the scheduled end time (so it reappears next campaign)
          localStorage.setItem(this.storageKey, String(this.endMs || Date.now()))
        } catch (e) { /* ignore */ }
      }
    }
  }
}
</script>

<style scoped>
.maintenance-notifier {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 0.85rem 2.5rem 0.85rem 1rem;
  background: #ffe7b8; /* subtle amber */
  color: #3a2a00;
  font-weight: 600;
  text-align: center;
  z-index: 99999;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}
.mn-message {
  display: inline-block;
}
.close-maintenance {
  position: absolute;
  right: 0.5rem;
  top: 0.35rem;
  border: 0;
  background: transparent;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
}
.close-maintenance:hover { opacity: 1; }

/* simple fade */
.mn-fade-enter-active, .mn-fade-leave-active {
  transition: opacity .25s ease;
}
.mn-fade-enter, .mn-fade-leave-to {
  opacity: 0;
}
</style>